import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { getOwner } from '@ember/owner';
import { on } from '@ember/modifier';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { macroCondition, isDevelopingApp } from '@embroider/macros';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import { HashicorpCloudIamSSOType } from '@clients/cloud-iam';
import {
  HdsPageHeader,
  HdsBadge,
  HdsButton,
  HdsDropdown,
  HdsApplicationState,
  HdsAlert,
} from '@hashicorp/design-system-components/components';
import {
  CoreLayoutMain,
  BreadcrumbsContainer,
  Pagination,
} from 'core/components';
import { boxPadding } from 'core/modifiers';
import { urlFor } from 'core/helpers';
import {
  SCIM_ENABLED_PROVIDERS,
  SystemNotifications,
} from 'core/utils/constants';
import Enabled from 'hcp/components/scim/enabled';
import Disable from 'hcp/components/scim/disable';
import List from 'hcp/components/scim/tokens/list';
import Create from 'hcp/components/scim/tokens/create';

export default class PageOrganizationsDetailScim extends Component {
  @service abilities;
  @service api;
  @service flashMessages;
  @service intl;
  @service router;

  @tracked showDisableModal = false;
  @tracked showTokenModal = false;

  get canEnableScim() {
    return this.abilities.can('enable scim');
  }

  get isSsoDisabled() {
    const { ssoEnabled } = this.args;
    return !ssoEnabled;
  }

  get isSaml() {
    const { ssoType } = this.args;
    return ssoType === HashicorpCloudIamSSOType.SAML;
  }

  get shouldShowEnableButton() {
    const { scimEnabled } = this.args;

    // Show the enable button only if SCIM isn't already enabled, it's a SAML SSO connection and the the user has permission to enable SCIM.
    return !scimEnabled && this.isSaml && this.canEnableScim;
  }

  get canDisableScim() {
    const { scimEnabled } = this.args;
    return this.abilities.can('disable scim', undefined, { scimEnabled });
  }

  enableSCIM = task(async () => {
    const { organization } = this.args;
    try {
      await this.api.scim.scimServiceEnableScim(organization.id);
      this.flashMessages.success(
        this.intl.t(
          'components.page.organizations.detail.scim.enable.success.title'
        ),
        {
          content: this.intl.t(
            'components.page.organizations.detail.scim.enable.success.message'
          ),
        }
      );
      this.refreshRoute();
    } catch (e) {
      if (macroCondition(isDevelopingApp())) {
        console.error(e);
      }
      this.flashMessages.error(
        this.intl.t(
          'components.page.organizations.detail.scim.enable.error.title'
        ),
        {
          content: this.intl.t(
            'components.page.organizations.detail.scim.enable.error.message'
          ),
        }
      );
    }
  });

  disableSCIM = task(async () => {
    const { organization } = this.args;
    try {
      await this.api.scim.scimServiceDisableScim(organization.id);
      this.flashMessages.success(
        this.intl.t(
          'components.page.organizations.detail.scim.disable.success.message'
        )
      );
      this.closeDisableModal();
      this.refreshRoute();
    } catch (e) {
      if (macroCondition(isDevelopingApp())) {
        console.error(e);
      }
      this.flashMessages.error(
        this.intl.t(
          'components.page.organizations.detail.scim.disable.error.title'
        ),
        {
          content: this.intl.t(
            'components.page.organizations.detail.scim.disable.error.message'
          ),
        }
      );
    }
  });

  openDisableModal = () => {
    this.showDisableModal = true;
  };

  closeDisableModal = () => {
    if (this.isDestroyed || this.isDestroying) return;
    this.showDisableModal = false;
  };

  openTokenModal = () => {
    this.showTokenModal = true;
  };

  closeTokenModal = () => {
    if (this.isDestroyed || this.isDestroying) return;
    this.showTokenModal = false;
    this.refreshRoute();
  };

  refreshRoute = () => {
    let route = getOwner(this).lookup(
      `route:${this.router.currentRoute.parent.name}`
    );
    return route.refresh();
  };

  showPrivateBetaNotification = () => {
    this.flashMessages.systemNotification(
      SystemNotifications.SCIM_PRIVATE_BETA,
      { providers: SCIM_ENABLED_PROVIDERS }
    );
  };

  <template>
    <CoreLayoutMain {{didInsert this.showPrivateBetaNotification}}>
      <:header>
        <HdsPageHeader as |PH|>
          <PH.Breadcrumb>
            <BreadcrumbsContainer />
          </PH.Breadcrumb>
          <PH.Title data-test-title>
            {{t "components.page.organizations.detail.scim.header.title"}}
            <HdsBadge
              @text={{t
                "components.page.organizations.detail.scim.header.beta-badge"
              }}
              @color="highlight"
              data-test-scim-header-beta-badge
            />
          </PH.Title>
          <PH.Description>
            {{t "components.page.organizations.detail.scim.header.description"}}
          </PH.Description>
          <PH.Actions>
            {{#if this.shouldShowEnableButton}}
              <HdsButton
                @icon="power"
                @iconPosition="leading"
                @text={{t
                  "components.page.organizations.detail.scim.empty-state.action"
                }}
                data-test-scim-header-enable-button
                {{on "click" this.enableSCIM.perform}}
              />
            {{else if this.canDisableScim}}
              <HdsDropdown as |dd|>
                <dd.ToggleButton
                  @color="secondary"
                  @text={{t
                    "components.page.organizations.detail.scim.disable.title"
                  }}
                  data-test-scim-manage
                />
                <dd.Interactive
                  @color="critical"
                  @icon="trash"
                  @text={{t
                    "components.page.organizations.detail.scim.disable.action"
                  }}
                  {{on "click" this.openDisableModal}}
                  data-test-scim-disable
                />
              </HdsDropdown>
            {{/if}}
          </PH.Actions>
        </HdsPageHeader>
      </:header>
      <:body>
        {{#if @scimEnabled}}
          <Enabled
            @organization={{@organization}}
            @ssoType={{@ssoType}}
            @scimTenantUrl={{@scimTenantUrl}}
          />
          <List
            @openTokenModal={{this.openTokenModal}}
            @tokens={{@tokens}}
            @organization={{@organization}}
          />
          <Pagination @margin="sm 0 0 0" @pagination={{@pagination}} />
        {{else}}
          <HdsApplicationState {{boxPadding "2xl 0"}} as |A|>
            <A.Header
              @title={{t
                "components.page.organizations.detail.scim.empty-state.header"
              }}
              data-test-scim-empty-state-header
            />
            <A.Body
              @text={{t
                "components.page.organizations.detail.scim.empty-state.body"
              }}
              data-test-scim-empty-state-body
            />
            <HdsAlert @type="compact" {{boxPadding "md 0 0 0"}} as |Alert|>
              <Alert.Description data-test-scim-empty-state-alert-description>
                {{#if this.isSsoDisabled}}
                  {{t
                    "components.page.organizations.detail.scim.empty-state.alert.sso-disabled"
                    htmlSafe=true
                    enableSsoUrl=(urlFor "cloud.orgs.detail.settings.sso.index")
                  }}
                {{else}}
                  {{t
                    "components.page.organizations.detail.scim.empty-state.alert.sso-enabled"
                    ssoType=@ssoType
                  }}
                {{/if}}
              </Alert.Description>
            </HdsAlert>
            {{#if this.shouldShowEnableButton}}
              <A.Footer>
                <HdsButton
                  @color="tertiary"
                  @icon="power"
                  @text={{t
                    "components.page.organizations.detail.scim.empty-state.action"
                  }}
                  data-test-scim-empty-state-action
                  {{on "click" this.enableSCIM.perform}}
                />
              </A.Footer>
            {{/if}}
          </HdsApplicationState>
        {{/if}}
        {{#if this.showDisableModal}}
          <Disable
            @onCancel={{this.closeDisableModal}}
            @onSubmit={{this.disableSCIM}}
          />
        {{/if}}
        {{#if this.showTokenModal}}
          <Create
            @onCancel={{this.closeTokenModal}}
            @organization={{@organization}}
          />
        {{/if}}
      </:body>
    </CoreLayoutMain>
  </template>
}
